/* eslint-disable import/no-duplicates */
import React, { useState, useEffect, createContext } from 'react';

import { ThemeProvider } from 'styled-components';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ptBR from 'date-fns/locale/pt';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';

import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { IntlProvider } from 'react-intl';
import AccessControl from 'presentation/hooks/access';

import { Routes } from 'main/routes';
import store from 'data/store';
import Notifications from 'presentation/components/messages';
import { toast, ToastContainer } from 'react-toastify';
import { ConnectSocket } from 'infra/socket/ConnectSocket';
import { Skeleton } from '@mui/material';

import GlobalStyle from './presentation/base/styles/globals';
import { defaultTheme } from './presentation/base/themes';
import translations from './infra/i18n/locales';

import 'react-notifications-component/dist/theme.css';
import 'react-toastify/dist/ReactToastify.min.css';
import 'animate.css/animate.compat.css';

import './infra/global/variables';
import { makeRemoteMatchOrg } from './main/factories/usecases/org/MatchOrgFactory';

const persistor = persistStore(store);

export interface iSupportedLanguages {
  language:
    | 'pt-BR'
    | 'pt-br'
    | 'en-US'
    | 'en-us'
    | 'es'
    | 'es-ES'
    | 'es-es'
    | 'es-DO'
    | 'es-do'
    | 'es-GT'
    | 'es-gt'
    | 'es-HN'
    | 'es-hn'
    | 'es-MX'
    | 'es-mx'
    | 'es-PA'
    | 'es-pa'
    | 'es-PE'
    | 'es-pe'
    | 'es-SV'
    | 'es-SE'
    | 'es-sv'
    | 'es-se';
}

type ThemeContextProps = {
  theme: typeof defaultTheme;
  setTheme: (theme: typeof defaultTheme) => void;
};

export const ThemeContext = createContext<ThemeContextProps>(
  {} as ThemeContextProps,
);

function App(): JSX.Element {
  const [selectedLocale, setSelectedLocale] =
    useState<iSupportedLanguages['language']>('en-US');
  const [dateLocale, setDateLocale] = useState(ptBR);
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let userLanguage = localStorage.getItem('i18nextLng');

    if (!userLanguage) {
      userLanguage = navigator.language;
      localStorage.setItem('i18nextLng', userLanguage);
    }

    setSelectedLocale(userLanguage as iSupportedLanguages['language']);
    if (userLanguage.includes('pt')) setDateLocale(ptBR);
    if (userLanguage.includes('en')) setDateLocale(enUS);
    if (userLanguage.includes('es')) setDateLocale(es);

    setLoading(true);

    makeRemoteMatchOrg()
      .match({
        url: window.location.origin,
      })
      .then(res => {
        if (res && res.skin) {
          setTheme(res.skin);

          let link: HTMLLinkElement | null =
            document.querySelector("link[rel~='icon']");

          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
          }

          document.title = res.skin.emailParams.systemName;
          link.href = res.skin.images.logo.small;
        }
      })
      .catch(err => {
        // toast.error('Não foi possível carregar a skin');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (
      window.location.pathname.includes('/conf') ||
      window.location?.ancestorOrigins.length > 0
    )
      return;

    setTimeout(() => {
      const headID = document.getElementsByTagName('head')[0];
      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';
      newScript.src =
        'https://api.assistatecnologia.com.br/player/assets/js/assista-plugin.min.js';

      headID.appendChild(newScript);
    }, 7000);
  }, []);

  return (
    <IntlProvider
      messages={translations[selectedLocale]}
      locale={String(selectedLocale)}
      defaultLocale="pt-BR"
    >
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <AccessControl>
            <ConnectSocket>
              <ThemeContext.Provider value={{ theme, setTheme }}>
                <ThemeProvider theme={theme}>
                  <Notifications>
                    <MuiPickersUtilsProvider
                      utils={DateFnsUtils}
                      locale={dateLocale}
                    >
                      <GlobalStyle />
                      <ToastContainer />
                      {loading ? (
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                          animation="wave"
                        />
                      ) : (
                        <Routes />
                      )}
                    </MuiPickersUtilsProvider>
                  </Notifications>
                </ThemeProvider>
              </ThemeContext.Provider>
            </ConnectSocket>
          </AccessControl>
        </PersistGate>
      </Provider>
    </IntlProvider>
  );
}

export default App;
